import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { IDeviceTeleopConfiguration } from "@formant/core/model/IDeviceTeleopConfiguration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { rosTypeTwist } from "@formant/core/model/rosTypes";
import { rtcTwistStreamType } from "@formant/core/model/rtcStreamTypes";
import { Fader } from "@formant/ui/component/common/Fader";
import { Measure } from "@formant/ui/component/common/Measure";

import { operateStore } from "../../stores";
import { ITeleopSession } from "../../teleop/ITeleopSession";
import { getBitsetsFromStreamConfig } from "../../utils/getBitsetsFromStreamConfig";
import { teleopStreamToRtc } from "../../utils/teleopStreamToRtc";
import { Accordion } from "../common/Accordion";
import * as styles from "./TeleopControls.scss";
import { TeleopCommands } from "./TeleopModule/TeleopCommands";
import { TeleopJoysticks } from "./TeleopModule/TeleopJoysticks";

const bottomMargin = 60;

export interface ITeleopControlsProps {
    session: ITeleopSession;
    configuration: IDeviceTeleopConfiguration;
    sticky?: boolean;
}

@observer
export class TeleopControls extends Component<ITeleopControlsProps> {
    public render() {
        const { session, configuration, sticky } = this.props;
        const { customStreams, rosStreams, joysticks } = configuration;
        const { deviceId } = session;
        const { share, armed } = operateStore;

        const configuredVelocityStream = joysticks?.length
            ? joysticks[0].velocityStreamName
            : undefined;

        const customVelocityStream = (customStreams || []).find(
            _ =>
                _.rtcStreamType === rtcTwistStreamType &&
                _.mode === "command" &&
                (configuredVelocityStream
                    ? _.valueStreamName === configuredVelocityStream
                    : true),
        );
        const rosVelocityStream = (rosStreams || []).find(
            _ =>
                _.topicType === rosTypeTwist &&
                _.mode === "command" &&
                (configuredVelocityStream
                    ? _.topicName === configuredVelocityStream
                    : true),
        );

        const velocityStream: IRtcStream | undefined = customVelocityStream
            ? {
                  streamName: customVelocityStream.name,
                  streamType: rtcTwistStreamType,
                  entityId: deviceId,
              }
            : rosVelocityStream
              ? teleopStreamToRtc(deviceId, rosVelocityStream)
              : undefined;

        const buttonStreams: IRtcStream[] = getBitsetsFromStreamConfig(
            deviceId,
            configuration,
            "command",
        );

        const hasControls = !!velocityStream || buttonStreams.length > 0;
        const visible = armed && hasControls;

        return (
            <Fader
                className={classnames(
                    styles["teleop-controls"],
                    share && styles.share,
                    sticky && styles.sticky,
                )}
                visible={
                    buttonStreams.length > 0 ||
                    (velocityStream !== undefined && joysticks !== undefined)
                }
            >
                <Measure onMeasureResize={this.onResize}>
                    <Accordion
                        visible={visible}
                        maxHeight={800}
                        className={styles.accordion}
                    >
                        {buttonStreams.length > 0 && (
                            <TeleopCommands
                                session={session}
                                configuration={configuration}
                                streams={buttonStreams}
                                className={styles.commands}
                            />
                        )}

                        {velocityStream && joysticks && (
                            <TeleopJoysticks
                                velocityStream={velocityStream}
                                session={session}
                                className={styles.joysticks}
                                configuration={joysticks}
                            />
                        )}
                    </Accordion>
                </Measure>
            </Fader>
        );
    }
    private onResize = (width: number, height: number) => {
        operateStore.controlsDimensions = {
            x: width,
            y: height + bottomMargin,
        };
    };
}
